"use client";

import NextImage from "next/image";
import Link from "next/link";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import TurnstileChallenge from "@/components/turnstile-challenge";
import {
  DASHBOARD_TEST_ENDPOINTS,
  PUBLIC_TEST_ENDPOINTS,
  type DashboardTestEndpoint,
} from "@/lib/test/constants";
import { type ServiceCapabilities } from "@/lib/capabilities";
import { getApiErrorMessage } from "@/lib/api/errors";
import { isCompletedTestResult } from "@/lib/test/result";
import { captureProductEvent } from "@/lib/posthog/product-events";
import EndpointTooltip from "@/components/endpoint-tooltip";
import styles from "./test-studio.module.css";
import { Button } from "@/components/ui/button";
import { Braces, ImageIcon, Upload, ArrowRight, LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

/* Hallmark · component: API workbench · genre: modern-minimal · theme: existing deepface.dev
 * states: default · hover · focus · active · disabled · loading · error · success
 * pre-emit critique: P4 H4 E4 S5 R4 V4
 */

type TestStudioMode = "public" | "dashboard";

export type TestStudioCompletion = {
  source: "sample" | "customer";
  endpoint: DashboardTestEndpoint;
  modelName: string;
  progressSaved: boolean;
};

type TestStudioProps = {
  mode: TestStudioMode;
  apiKeyId?: number;
  apiKeyLabel?: string;
  initialCapabilities?: CapabilitiesResponse;
  isActive?: boolean;
  onClose?: () => void;
  guided?: boolean;
  compact?: boolean;
  initialInputSource?: "sample" | "own";
  prefillComparisonPhoto?: boolean;
  onCompleted?: (completion: TestStudioCompletion) => void;
  onRunningChange?: (running: boolean) => void;
};

type PublicQuota = {
  enabled: boolean;
  limit: number;
  remaining: number;
  blocked: boolean;
  missing?: string[];
};

type CapabilitiesResponse = ServiceCapabilities;

type CompareSettings = {
  targetVector: string;
  threshold: string;
  metric: "cosine" | "euclidean" | "euclidean_l2";
};

type TesterErrorState = {
  code: string | null;
  detail: string | null;
  status: number | null;
  payload: Record<string, unknown> | null;
};

type ImageUploadFieldProps = {
  label: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  prominent?: boolean;
  presetImage?: string;
};

function isPublicQuota(value: unknown): value is PublicQuota {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.enabled === "boolean" &&
    typeof record.limit === "number" &&
    typeof record.remaining === "number" &&
    typeof record.blocked === "boolean"
  );
}

function isCapabilitiesResponse(value: unknown): value is CapabilitiesResponse {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    Array.isArray(record.allowed_models) &&
    typeof record.default_model === "string" &&
    Array.isArray(record.allowed_detectors) &&
    typeof record.default_detector === "string"
  );
}

function getInitialModelName(
  capabilities: CapabilitiesResponse | null | undefined
): string {
  if (!capabilities || !isCapabilitiesResponse(capabilities)) {
    return "";
  }

  return capabilities.default_model || capabilities.allowed_models[0] || "";
}

const endpointLabels: Record<DashboardTestEndpoint, { title: string; subtitle: string }> = {
  "/verify": {
    title: "Verify",
    subtitle: "Compare two photos",
  },
  "/represent": {
    title: "Represent",
    subtitle: "Generate one embedding",
  },
  "/compare": {
    title: "Compare",
    subtitle: "Image-to-vector compare",
  },
};

const PUBLIC_TEST_TARGET_REQUEST_BYTES = 3_600_000;
const PUBLIC_TEST_MAX_IMAGE_DIMENSION = 1600;
const PUBLIC_TEST_MIN_IMAGE_DIMENSION = 960;
const PUBLIC_TEST_JPEG_QUALITIES = [0.86, 0.78, 0.7, 0.62, 0.54] as const;

function normalizeUploadFileName(name: string): string {
  return name.replace(/\.[a-z0-9]+$/i, "") + ".jpg";
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => {
      URL.revokeObjectURL(url);
    };

    image.onload = () => {
      cleanup();
      resolve(image);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error("image_load_failed"));
    };
    image.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("image_encode_failed"));
      },
      type,
      quality
    );
  });
}

async function optimizeImageForPublicTester(
  file: File,
  targetBytes: number
): Promise<File> {
  if (typeof window === "undefined" || !file.type.toLowerCase().startsWith("image/")) {
    return file;
  }

  try {
    const image = await loadImageElement(file);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    const longestSide = Math.max(sourceWidth, sourceHeight, 1);

    if (
      file.size <= targetBytes &&
      longestSide <= PUBLIC_TEST_MAX_IMAGE_DIMENSION &&
      file.type === "image/jpeg"
    ) {
      return file;
    }

    let maxDimension = Math.min(PUBLIC_TEST_MAX_IMAGE_DIMENSION, longestSide);
    let best = file;

    while (true) {
      const scale = Math.min(1, maxDimension / longestSide);
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        return best;
      }

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      for (const quality of PUBLIC_TEST_JPEG_QUALITIES) {
        const blob = await canvasToBlob(canvas, "image/jpeg", quality);
        const candidate = new File([blob], normalizeUploadFileName(file.name), {
          type: "image/jpeg",
        });

        if (candidate.size < best.size) {
          best = candidate;
        }

        if (candidate.size <= targetBytes) {
          return candidate;
        }
      }

      if (maxDimension <= PUBLIC_TEST_MIN_IMAGE_DIMENSION) {
        return best;
      }

      const nextDimension = Math.max(
        PUBLIC_TEST_MIN_IMAGE_DIMENSION,
        Math.round(maxDimension * 0.82)
      );
      if (nextDimension === maxDimension) {
        return best;
      }
      maxDimension = nextDimension;
    }
  } catch {
    return file;
  }
}

function formatNumber(value: unknown, digits = 4): string {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "--";
  }
  return num.toFixed(digits);
}

function getErrorMessage(errorCode: string | null): string | null {
  if (!errorCode) {
    return null;
  }
  if (errorCode === "preview_inference_unavailable") return "This preview can’t analyse photos. Use the sample to see a simulated response.";
  if (errorCode === "request_pending") return "Request accepted, but no completed result was returned yet.";
  if (errorCode === "incomplete_result") return "The API replied without a usable result. This attempt is not marked complete.";
  if (errorCode === "connection_interrupted") return "We lost the connection before receiving a result.";
  return getApiErrorMessage(errorCode) || null;
}

function asErrorPayload(
  value: Record<string, unknown> | { error?: string; remaining?: number; limit?: number } | null
): Record<string, unknown> | null {
  if (!value || Array.isArray(value)) {
    return null;
  }
  return value;
}

function getPayloadDetail(payload: Record<string, unknown> | null): string | null {
  if (!payload) {
    return null;
  }
  const detail = payload.detail;
  return typeof detail === "string" && detail.trim() ? detail.trim() : null;
}

function buildTesterErrorState(input: {
  code: string | null;
  detail?: string | null;
  status?: number | null;
  payload?: Record<string, unknown> | { error?: string; remaining?: number; limit?: number } | null;
}): TesterErrorState {
  const payload = asErrorPayload(input.payload ?? null);
  return {
    code: input.code,
    detail: input.detail?.trim() || getPayloadDetail(payload),
    status: input.status ?? null,
    payload,
  };
}

function parseRepresentSummary(result: unknown) {
  const rows = Array.isArray(result)
    ? result
    : result && typeof result === "object" && Array.isArray((result as { result?: unknown }).result)
      ? ((result as { result: unknown[] }).result ?? [])
      : result
        ? [result]
        : [];
  const faces = rows.filter((item) => item && typeof item === "object") as Array<Record<string, unknown>>;
  const firstFace = faces[0] ?? null;
  const embedding = firstFace?.embedding ?? firstFace?.vector;
  const vectorLength = Array.isArray(embedding) ? embedding.length : 0;
  const modelName =
    (typeof firstFace?.model_name === "string" && firstFace.model_name) ||
    (typeof (result as { model_name?: unknown })?.model_name === "string" &&
      (result as { model_name?: string }).model_name) ||
    null;

  return {
    faceCount: faces.length,
    vectorLength,
    modelName,
  };
}

function parseCompareSummary(result: unknown) {
  if (!result || typeof result !== "object") {
    return null;
  }
  const value = result as Record<string, unknown>;
  const best = value.best_match;
  const bestDistance =
    best && typeof best === "object"
      ? (best as Record<string, unknown>).distance
      : null;

  const rows = Array.isArray(value.results) ? value.results.length : 0;

  return {
    metric: typeof value.metric === "string" ? value.metric : "--",
    rows,
    bestDistance,
  };
}

function ImageUploadField({ label, file, onFileChange, prominent = false, presetImage }: ImageUploadFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const displayedUrl = previewUrl ?? presetImage;
  const handleRemove = () => {
    onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className={cn("min-w-0 space-y-2 text-xs text-[var(--muted)]", prominent && styles.uploadField)}>
      <label className="uppercase tracking-[0.3em]" htmlFor={inputId}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        className="peer sr-only"
      />
      <div className={cn("flex flex-wrap items-center gap-3 rounded-xl peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-[var(--accent)]", prominent && styles.uploadArea)}>
        {displayedUrl ? (
          <div className={cn("relative shrink-0", prominent ? styles.uploadPreview : "h-24 w-24")}>
            <div className="relative h-full w-full overflow-hidden rounded-lg border border-border bg-background">
              <NextImage
                src={displayedUrl}
                alt={`${label} preview`}
                fill
                unoptimized
                sizes="(min-width: 640px) 96px, 80px"
                className={prominent ? styles.uploadPreviewImage : "object-cover"}
              />
            </div>
            <button
              type="button"
              onClick={handleRemove}
              aria-label={`Remove ${label.toLowerCase()}`}
              className="absolute -right-3 -top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background p-0 text-foreground transition hover:border-[var(--accent)] hover:text-[var(--accent)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 12 12"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M2 2 10 10" />
                <path d="M10 2 2 10" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            <label
              htmlFor={inputId}
              className={cn("flex h-24 w-24 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-dashed border-border bg-background text-[var(--text)] transition hover:border-[var(--accent)] hover:bg-[var(--accent)]/10", prominent && styles.uploadTrigger)}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 15V4" />
                <path d="m8 8 4-4 4 4" />
                <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
              </svg>
              {prominent ? <span>Choose photo</span> : null}
            </label>
            {prominent ? null : <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">
              JPG / PNG / WEBP
            </span>}
          </>
        )}
      </div>
      {file || presetImage ? <><p className="break-all text-xs text-[var(--muted)]">{file?.name ?? "Demo photo"}</p>{prominent ? <label htmlFor={inputId} className={styles.changePhoto}>Change photo</label> : null}</> : null}
    </div>
  );
}

export default function TestStudio({
  mode,
  apiKeyId,
  apiKeyLabel,
  initialCapabilities,
  isActive = true,
  onClose,
  guided = false,
  compact = false,
  initialInputSource,
  prefillComparisonPhoto = false,
  onCompleted,
  onRunningChange,
}: TestStudioProps) {
  const isCompact = guided && compact;
  const isPlayground = mode === "public" && isCompact;
  const SampleHeading = isPlayground ? "h3" : "h4";
  const localPreview = process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_ONBOARDING_PREVIEW === "1";
  const endpointOptions = mode === "public" ? PUBLIC_TEST_ENDPOINTS : DASHBOARD_TEST_ENDPOINTS;
  const validatedInitialCapabilities = isCapabilitiesResponse(initialCapabilities)
    ? initialCapabilities
    : null;
  const [endpoint, setEndpoint] = useState<DashboardTestEndpoint>(guided ? "/represent" : endpointOptions[0]);
  const [inputSource, setInputSource] = useState<"sample" | "own">(initialInputSource ?? (guided ? "sample" : "own"));
  const [capabilities, setCapabilities] = useState<CapabilitiesResponse | null>(
    validatedInitialCapabilities
  );
  const [modelName, setModelName] = useState<string>(
    getInitialModelName(validatedInitialCapabilities)
  );
  const [img, setImg] = useState<File | null>(null);
  const [img1, setImg1] = useState<File | null>(null);
  const [useDemoComparisonPhoto, setUseDemoComparisonPhoto] = useState(prefillComparisonPhoto);
  const [img2, setImg2] = useState<File | null>(null);
  const [compareSettings, setCompareSettings] = useState<CompareSettings>({
    targetVector: "",
    threshold: "",
    metric: "cosine",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState<TesterErrorState | null>(null);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [requestPhase, setRequestPhase] = useState<"preparing" | "waiting">("preparing");
  const [resultSource, setResultSource] = useState<"sample" | "own">("own");
  const resultRef = useRef<HTMLElement | null>(null);
  const modelSelectId = useId();
  const [quota, setQuota] = useState<PublicQuota | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [capabilitiesRetry, setCapabilitiesRetry] = useState(0);
  const modelOptions = capabilities?.allowed_models ?? [];

  const errorMessage = useMemo(
    () => getErrorMessage(errorState?.code ?? null),
    [errorState?.code]
  );

  useEffect(() => {
    onRunningChange?.(isLoading);
  }, [isLoading, onRunningChange]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (validatedInitialCapabilities) {
      setCapabilities(validatedInitialCapabilities);
      setModelName((current) => {
        if (
          current &&
          validatedInitialCapabilities.allowed_models.includes(current)
        ) {
          return current;
        }

        return (
          validatedInitialCapabilities.default_model ||
          validatedInitialCapabilities.allowed_models[0] ||
          ""
        );
      });
    }

    let mounted = true;
    const showCapabilitiesLoadError = () => {
      if (validatedInitialCapabilities) {
        return;
      }
      setErrorState(
        buildTesterErrorState({
          code: "upstream_failed",
          detail: "Could not load model capabilities.",
        })
      );
    };

    const loadCapabilities = async () => {
      try {
        const response = await fetch("/api/capabilities", { cache: "no-store" });
        const payload = await response.json().catch(() => null);
        if (!mounted) return;
        if (!response.ok || !isCapabilitiesResponse(payload)) {
          showCapabilitiesLoadError();
          return;
        }
        setCapabilities(payload);
        setModelName((current) => {
          if (current && payload.allowed_models.includes(current)) {
            return current;
          }
          return payload.default_model || payload.allowed_models[0] || "";
        });
      } catch {
        if (!mounted) return;
        showCapabilitiesLoadError();
      }
    };
    void loadCapabilities();
    return () => {
      mounted = false;
    };
  }, [validatedInitialCapabilities, isActive, capabilitiesRetry]);

  useEffect(() => {
    if (mode !== "public" || !isActive) {
      return;
    }

    let mounted = true;
    const loadQuota = async () => {
      try {
        const response = await fetch("/api/test/public", { cache: "no-store" });
        const payload = await response.json().catch(() => null);
        if (!mounted) return;
        if (!response.ok || !isPublicQuota(payload)) {
          let code = "";
          let detail: string | null = null;
          if (payload && typeof payload === "object" && "error" in payload) {
            code = String((payload as { error?: string }).error || "");
            if (
              code === "test_config_missing" &&
              Array.isArray((payload as { missing?: unknown }).missing)
            ) {
              const missing = (payload as { missing: unknown[] }).missing
                .map((item) => String(item))
                .filter(Boolean);
              if (missing.length > 0) {
                detail = `Missing env vars: ${missing.join(", ")}`;
              }
            }
          }
          setErrorState(
            buildTesterErrorState({
              code: code || "public_test_unavailable",
              detail,
            })
          );
          return;
        }
        if (!payload.enabled) {
          const missing = Array.isArray(payload.missing)
            ? payload.missing.map((item) => String(item)).filter(Boolean)
            : [];
          setErrorState(
            buildTesterErrorState({
              code: "test_config_missing",
              detail: missing.length > 0 ? `Missing env vars: ${missing.join(", ")}` : null,
            })
          );
        }
        setQuota(payload);
      } catch {
        if (!mounted) return;
        setErrorState(
          buildTesterErrorState({
            code: "public_test_unavailable",
          })
        );
      }
    };

    void loadQuota();
    return () => {
      mounted = false;
    };
  }, [isActive, mode]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const startedAt = Date.now();
    setElapsedSeconds(0);
    const timer = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isLoading]);

  useEffect(() => {
    if (result && guided) resultRef.current?.focus({ preventScroll: true });
  }, [result, guided]);

  useEffect(() => {
    setErrorState(null);
    setResult(null);
    setShowRawJson(false);
    if (endpoint !== "/verify") {
      setImg1(null);
      setImg2(null);
    }
    if (endpoint !== "/represent" && endpoint !== "/compare") {
      setImg(null);
    }
  }, [endpoint]);

  const isBlocked = mode === "public" && quota?.blocked;

  const handleSubmit = async () => {
    if (isLoading) return;
    if (mode === "dashboard" && (!apiKeyId || apiKeyId <= 0)) {
      setErrorState(buildTesterErrorState({ code: "invalid_key_id" }));
      return;
    }
    if (!modelName || modelOptions.length === 0) {
      setErrorState(buildTesterErrorState({ code: "model_not_allowed" }));
      return;
    }
    if (mode === "public" && !captchaToken) {
      setErrorState(buildTesterErrorState({ code: "captcha_required" }));
      return;
    }

    setErrorState(null);
    setResult(null);
    setRequestPhase("preparing");
    setIsLoading(true);

    const formData = new FormData();
    formData.append("endpoint", endpoint);
    formData.append("model_name", modelName);
    formData.append("input_source", inputSource);
    if (inputSource === "sample") formData.append("sample_id", "synthetic-portrait-v1");
    const publicVerifyTargetBytes = Math.floor(PUBLIC_TEST_TARGET_REQUEST_BYTES / 2);
    const publicSingleImageTargetBytes = PUBLIC_TEST_TARGET_REQUEST_BYTES;

    if (endpoint === "/verify") {
      if ((!img1 && !useDemoComparisonPhoto) || !img2) {
        setErrorState(buildTesterErrorState({ code: "missing_image_input" }));
        setIsLoading(false);
        return;
      }
      let firstPhoto = img1;
      if (!firstPhoto && useDemoComparisonPhoto) {
        try {
          const demo = await fetch("/samples/synthetic-portrait-v1.png");
          if (!demo.ok) throw new Error("demo_photo_unavailable");
          firstPhoto = new File([await demo.blob()], "demo-photo.png", { type: "image/png" });
        } catch {
          setErrorState(buildTesterErrorState({ code: "missing_image_input", detail: "Couldn’t load the demo photo. Choose your own photo and try again." }));
          setIsLoading(false);
          return;
        }
      }
      const uploadImg1 =
        mode === "public"
          ? await optimizeImageForPublicTester(firstPhoto!, publicVerifyTargetBytes)
          : firstPhoto!;
      const uploadImg2 =
        mode === "public"
          ? await optimizeImageForPublicTester(img2, publicVerifyTargetBytes)
          : img2;
      formData.append("img1", uploadImg1);
      formData.append("img2", uploadImg2);
    }

    if (endpoint === "/represent" && inputSource === "own") {
      if (!img) {
        setErrorState(buildTesterErrorState({ code: "missing_image_input" }));
        setIsLoading(false);
        return;
      }
      const uploadImg =
        mode === "public"
          ? await optimizeImageForPublicTester(img, publicSingleImageTargetBytes)
          : img;
      formData.append("img", uploadImg);
    }

    if (endpoint === "/compare") {
      if (!img || !compareSettings.targetVector.trim()) {
        setErrorState(buildTesterErrorState({ code: "invalid_target_vector" }));
        setIsLoading(false);
        return;
      }
      const uploadImg = await optimizeImageForPublicTester(img, publicSingleImageTargetBytes);
      formData.append("img", uploadImg);
      formData.append("target_vector", compareSettings.targetVector.trim());
      formData.append("distance_metric", compareSettings.metric);
      if (compareSettings.threshold.trim()) {
        formData.append("threshold", compareSettings.threshold.trim());
      }
    }

    if (mode === "dashboard") {
      formData.append("api_key_id", String(apiKeyId));
    }
    if (mode === "public") {
      formData.append("turnstile_token", captchaToken as string);
    }

    try {
      setRequestPhase("waiting");
      if (guided) captureProductEvent("activation_request_started", { surface: "account", method: endpoint, result: inputSource });
      const response = await fetch(
        mode === "public" ? "/api/test/public" : "/api/test/key",
        {
          method: "POST",
          body: formData,
        }
      );

      const payload = (await response.json().catch(() => null)) as
        | Record<string, unknown>
        | null;

      if (!response.ok) {
        if (guided) captureProductEvent("activation_request_failed", { surface: "account", method: endpoint, result: inputSource, reason: `http_${response.status}` });
        const fallbackCode =
          response.status === 413 ? "request_too_large" : "upstream_failed";
        const code = (payload as { error?: string } | null)?.error || fallbackCode;
        let detail: string | null = null;
        if (payload && typeof payload === "object") {
          const payloadDetail = (payload as { detail?: unknown }).detail;
          if (typeof payloadDetail === "string" && payloadDetail.trim()) {
            detail = payloadDetail.trim();
          }
        }
        if (!detail && response.status === 413) {
          detail =
            mode === "public"
              ? "The public tester sits behind a smaller request-body limit than the API. Large uploads are auto-resized now, but extremely large originals can still exceed it."
              : "Keep all uploaded images below 4.5 MB total, combined.";
        }
        if (
          code === "test_config_missing" &&
          payload &&
          typeof payload === "object" &&
          Array.isArray((payload as { missing?: unknown }).missing)
        ) {
          const missing = (payload as { missing: unknown[] }).missing
            .map((item) => String(item))
            .filter(Boolean);
          detail = missing.length > 0 ? `Missing env vars: ${missing.join(", ")}` : detail;
        }
        setErrorState(
          buildTesterErrorState({
            code,
            detail,
            status: response.status,
            payload,
          })
        );
        if (mode === "public" && payload && "remaining" in payload) {
          setQuota((previous) => ({
            enabled: previous?.enabled ?? true,
            blocked: Number(payload.remaining ?? 0) <= 0,
            remaining: Number(payload.remaining ?? 0),
            limit: Number(payload.limit ?? previous?.limit ?? 2),
          }));
        }
        return;
      }

      if (!isCompletedTestResult(endpoint, response.status, payload)) {
        if (guided) captureProductEvent("activation_request_failed", { surface: "account", method: endpoint, result: inputSource, reason: response.status === 202 ? "pending" : "incomplete_result" });
        setErrorState(buildTesterErrorState({
          code: response.status === 202 ? "request_pending" : "incomplete_result",
          detail: response.status === 202
            ? "An accepted job is not a completed API result. Check your usage before submitting another request, or contact support with the response details."
            : "You can retry this request. If this happens again, share the response details with support; do not include your API key or images.",
          status: response.status,
          payload,
        }));
        return;
      }
      setResultSource(inputSource);
      setResult(payload as Record<string, unknown>);
      if (guided) captureProductEvent("activation_request_completed", { surface: "account", method: endpoint, result: inputSource });
      onCompleted?.({ source: inputSource === "sample" ? "sample" : "customer", endpoint, modelName, progressSaved: payload?.activation_progress_saved === true });
      setShowRawJson(false);

      if (mode === "public") {
        const remaining = Number((payload as { remaining?: unknown }).remaining ?? quota?.remaining ?? 0);
        const limit = Number((payload as { limit?: unknown }).limit ?? quota?.limit ?? 2);
        setQuota((previous) => ({
          enabled: previous?.enabled ?? true,
          remaining,
          limit,
          blocked: remaining <= 0,
        }));
      }
    } catch {
      if (guided) captureProductEvent("activation_request_failed", { surface: "account", method: endpoint, result: inputSource, reason: "connection_interrupted" });
      setErrorState(
        buildTesterErrorState({
          code: "connection_interrupted",
          detail: "The connection was interrupted. The server may still have processed the request. Check usage before retrying to avoid an extra call.",
        })
      );
    } finally {
      setIsLoading(false);
      if (mode === "public") {
        setCaptchaToken(null);
        setCaptchaResetKey((current) => current + 1);
      }
    }
  };

  const isSimulatedResult = result?.preview === true;
  const noFaceResult = result?.outcome === "no_face_detected";
  const verifyResult =
    endpoint === "/verify" && result && !noFaceResult
      ? {
          verified: Boolean(result.verified),
          distance: formatNumber(result.distance),
          threshold: formatNumber(result.threshold),
          modelName:
            (typeof result.model === "string" && result.model) ||
            (typeof result.model_name === "string" && result.model_name) ||
            modelName,
        }
      : null;

  const representSummary = endpoint === "/represent" && result && !noFaceResult ? parseRepresentSummary(result) : null;
  const compareSummary = endpoint === "/compare" && result && !noFaceResult ? parseCompareSummary(result) : null;

  const resultJsonToggle = (
    <Button
      variant="ghost"
      type="button"
      onClick={() => setShowRawJson((value) => !value)}
      className="min-h-11"
    >
      {showRawJson ? "Hide JSON" : "Show JSON"}
    </Button>
  );

  const pendingStatus = isLoading ? (
          <div role="status" className="space-y-2 rounded-xl border border-[var(--accent)]/30 bg-[var(--accent)]/5 p-4">
            <p className="text-sm text-[var(--text)]">{requestPhase === "preparing" ? "Preparing your images…" : isCompact ? "Waiting for the API…" : "Request sent. Waiting for the API’s response…"}</p>
            <p className="text-xs text-[var(--muted)]">{isCompact ? elapsedSeconds > 15 ? "Still waiting. The model may be starting. Keep this open." : "The first request can take a little longer." : elapsedSeconds > 15 ? "Still waiting. A cold start or a busy server can take longer. Keep this tab open; your request is still in progress." : "The first request may take longer while the model starts. We’ll show the result when it arrives."}</p>
            <p aria-live="off" className="font-mono text-xs text-[var(--muted)]">{elapsedSeconds}s elapsed</p>
          </div>
  ) : null;

  return (
    <div data-session-replay-block="true" className={cn(isPlayground ? styles.playground : "space-y-5", "min-w-0 [&_button]:whitespace-nowrap [&_button]:focus-visible:outline-2 [&_button]:focus-visible:outline-offset-4 [&_button]:focus-visible:outline-[var(--accent)] [&_button]:active:opacity-80 [&_select]:focus-visible:outline-2 [&_select]:focus-visible:outline-[var(--accent)] [&_textarea]:focus-visible:outline-2 [&_textarea]:focus-visible:outline-[var(--accent)] [&_input]:focus-visible:outline-2 [&_input]:focus-visible:outline-[var(--accent)]")}>
      {!isCompact || onClose ? <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {mode === "dashboard" && !isCompact ? (
            <h3 className="font-[var(--font-display)] text-2xl text-[var(--text)]">
              {guided ? "Make your first request" : "Test Studio"}
            </h3>
          ) : null}
          {mode === "dashboard" && !isCompact ? (
            <p className="mt-1 text-xs text-[var(--muted)]">
              {guided ? "Your key is connected securely. It stays out of the browser request." : `Running against key: ${apiKeyLabel || `#${apiKeyId}`}`}
            </p>
          ) : null}
        </div>

        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border bg-secondary px-4 py-2 text-xs font-[var(--font-display)] font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-ring"
          >
            Close
          </button>
        ) : null}
      </div> : null}

      {mode === "public" && isBlocked ? (
        <section className="rounded-2xl border border-[var(--accent)]/30 bg-[var(--surface)]/70 p-4">
          <p className="text-sm text-[var(--text)]">
            Test limit reached for this preview. Create an account for your own
            API key, or email us if you need help validating a higher-volume
            workflow.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <Link
              href="/signup?next=%2Faccount%2Fkeys"
              className="rounded-full bg-[var(--accent)] px-4 py-2 text-xs font-[var(--font-display)] font-semibold uppercase tracking-[0.3em] text-black transition hover:shadow-glow"
            >
              Get API Key
            </Link>
            <a
              href="mailto:hello@deepface.dev?subject=Public%20tester%20limit"
              className="rounded-full border border-border bg-secondary px-4 py-2 text-xs font-[var(--font-display)] font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-ring"
            >
              Contact support
            </a>
          </div>
        </section>
      ) : null}

      {errorMessage ? (
        <section role="alert" className="rounded-2xl border border-red-400/40 bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">{errorMessage}</p>
          {modelOptions.length === 0 ? <button type="button" className="mt-3 min-h-11 rounded-full border border-current px-4 text-sm" onClick={() => { setErrorState(null); setCapabilitiesRetry((count) => count + 1); }}>Reload models</button> : null}
          {errorState?.code === "request_pending" || errorState?.code === "incomplete_result" || errorState?.code === "connection_interrupted" ? (
            <p className="mt-2 text-sm">{errorState.detail}</p>
          ) : null}
          {errorState?.code === "unauthorized" ? (
            <Link href="/signin?next=%2Faccount" className="mt-3 inline-block min-h-11 underline">Sign in again</Link>
          ) : errorState?.code === "insufficient_credits" ? (
            <Link href="/account/billing" className="mt-3 inline-block min-h-11 underline">Check your plan and balance</Link>
          ) : null}
          {errorState?.detail && !isCompact ? (
            <p className="mt-2 text-xs text-destructive">
              Open technical details below for the exact gateway response.
            </p>
          ) : null}
          {(errorState?.status || errorState?.code || errorState?.detail || errorState?.payload) ? (
            <details className="mt-3 rounded-xl border border-destructive/40 bg-background p-3">
              <summary className="cursor-pointer text-xs font-[var(--font-display)] font-semibold uppercase tracking-[0.3em] text-destructive">
                Technical details
              </summary>
              <div className="mt-3 space-y-2 text-xs text-destructive">
                {errorState?.status ? (
                  <p>
                    <span className="font-semibold">HTTP status:</span> {errorState.status}
                  </p>
                ) : null}
                {errorState?.code ? (
                  <p>
                    <span className="font-semibold">Error code:</span> {errorState.code}
                  </p>
                ) : null}
                {errorState?.detail ? (
                  <p>
                    <span className="font-semibold">Exact detail:</span> {errorState.detail}
                  </p>
                ) : null}
                {errorState?.payload ? (
                  <pre className="overflow-x-auto rounded-xl border border-destructive/40 bg-background p-3 text-[11px] leading-5 text-destructive">
                    {JSON.stringify(errorState.payload, null, 2)}
                  </pre>
                ) : null}
              </div>
            </details>
          ) : null}
        </section>
      ) : null}

      <section className={cn(isPlayground && styles.requestPane, isCompact ? "min-w-0 space-y-4" : "min-w-0 space-y-4 rounded-2xl border border-border bg-secondary p-4 sm:p-5")} aria-busy={isLoading}>
        <fieldset disabled={isLoading} className="min-w-0 space-y-5 disabled:opacity-70">
        {guided ? (
          <div>
            <ToggleGroup type="single" value={inputSource} variant="outline" spacing={2} aria-label="Image source" disabled={isLoading}
              onValueChange={(value) => { if (value !== "sample" && value !== "own") return; setInputSource(value); if (value === "sample") setEndpoint("/represent"); setResult(null); setErrorState(null); }}
              className={styles.sourceOptions}>
              <ToggleGroupItem value="sample" className={styles.sourceOption}>{isPlayground ? <ImageIcon aria-hidden="true" /> : null}Use a sample</ToggleGroupItem>
              <ToggleGroupItem value="own" className={styles.sourceOption}>{isPlayground ? <Upload aria-hidden="true" /> : null}Use my images</ToggleGroupItem>
            </ToggleGroup>
            {inputSource === "sample" ? (
              <div className={isCompact ? styles.samplePreview : "mt-5 grid min-w-0 grid-cols-1 items-start gap-4 sm:grid-cols-[7rem_minmax(0,1fr)]"}>
                <NextImage src="/samples/synthetic-portrait-v1.png" alt="AI-generated sample portrait of a fictional adult" width={112} height={112} className={isCompact ? `${styles.samplePortrait} rounded-xl` : "aspect-square w-24 rounded-xl object-cover sm:w-full"} />
                <div className="min-w-0">
                  <SampleHeading className="text-lg text-[var(--text)]">{isPlayground ? "Sample photo" : "Try a sample photo."}</SampleHeading>
                  {isPlayground ? <code className={styles.requestEndpoint}>POST /represent</code> : null}
                  <p className="mt-1 text-sm text-[var(--muted)]">{localPreview ? "This preview shows a simulated response." : "This sample makes one real API call."}</p>
                </div>
              </div>
            ) : <p className="mt-3 text-sm text-[var(--muted)]">{localPreview ? "This preview can’t analyse uploads. Use the sample to see a simulated response." : "Use clear, front-facing photos you have permission to process."}</p>}
          </div>
        ) : null}
        {inputSource === "own" ? <div>
          <p className={isPlayground ? styles.fieldLabel : "text-xs uppercase tracking-[0.3em] text-[var(--muted)]"}>Endpoint</p>
          <div role="group" aria-label="Endpoint" className={cn("mt-2 flex flex-wrap gap-2", isPlayground && styles.endpointOptions)}>
            {endpointOptions.map((option) => {
              const selected = endpoint === option;
              const label = endpointLabels[option];
              return (
                <EndpointTooltip key={option} endpoint={option}>
                <button
                  type="button"
                  onClick={() => setEndpoint(option)}
                  aria-pressed={selected}
                  className={`${isPlayground ? styles.endpointOption : ""} rounded-2xl border px-4 py-2 text-left transition ${
                    selected
                      ? "border-[var(--accent)] bg-[var(--accent)]/15"
                      : "border-border bg-background hover:border-border"
                  }`}
                >
                  <p className="text-xs font-[var(--font-display)] font-semibold uppercase tracking-[0.3em] text-[var(--text)]">
                    {label.title}
                  </p>
                  <p className="mt-1 text-xs text-[var(--muted)]">{label.subtitle}</p>
                </button>
                </EndpointTooltip>
              );
            })}
          </div>
        </div> : null}

        {mode === "public" && isCompact ? null : <details open={isCompact ? undefined : true}>
          <summary className={isCompact ? "cursor-pointer text-sm text-[var(--muted)]" : "hidden"}>Model settings</summary>
        <div className={isCompact ? "mt-3" : undefined}>
          <label className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]" htmlFor={modelSelectId}>
            Model
          </label>
          <div className="relative mt-2 w-full max-w-md">
            <select
              id={modelSelectId}
              value={modelName}
              onChange={(event) => { setModelName(event.target.value); setResult(null); }}
              className="w-full appearance-none rounded-2xl border border-border bg-background px-3 py-2 pr-11 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
            >
              {modelOptions.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[var(--muted)]">
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m4 6 4 4 4-4" />
              </svg>
            </span>
          </div>
        </div>
        </details>}

        {inputSource === "sample" ? null : endpoint === "/verify" ? (
          <div className={cn("grid min-w-0 gap-5 sm:grid-cols-2", isPlayground && styles.uploads)}>
            <ImageUploadField prominent={isPlayground} label="Image 1" file={img1} presetImage={useDemoComparisonPhoto ? "/samples/synthetic-portrait-v1.png" : undefined} onFileChange={(file) => { setImg1(file); setUseDemoComparisonPhoto(false); setResult(null); }} />
            <ImageUploadField prominent={isPlayground} label="Image 2" file={img2} onFileChange={(file) => { setImg2(file); setResult(null); }} />
          </div>
        ) : (
          <ImageUploadField prominent={isPlayground} label="Image" file={img} onFileChange={(file) => { setImg(file); setResult(null); }} />
        )}

        {endpoint === "/compare" ? (
          <div className="grid gap-3">
            <label className="space-y-1 text-xs text-[var(--muted)]">
              <span className="uppercase tracking-[0.3em]">Target vector JSON</span>
              <textarea
                value={compareSettings.targetVector}
                onChange={(event) =>
                  setCompareSettings((current) => ({
                    ...current,
                    targetVector: event.target.value,
                  }))
                }
                rows={4}
                placeholder="[0.12, -0.02, ...]"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
              />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1 text-xs text-[var(--muted)]">
                <span className="uppercase tracking-[0.3em]">Threshold (optional)</span>
                <input
                  type="text"
                  value={compareSettings.threshold}
                  onChange={(event) =>
                    setCompareSettings((current) => ({
                      ...current,
                      threshold: event.target.value,
                    }))
                  }
                  placeholder="0.3"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
                />
              </label>

              <label className="space-y-1 text-xs text-[var(--muted)]">
                <span className="uppercase tracking-[0.3em]">Metric</span>
                <select
                  value={compareSettings.metric}
                  onChange={(event) =>
                    setCompareSettings((current) => ({
                      ...current,
                      metric: event.target.value as CompareSettings["metric"],
                    }))
                  }
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
                >
                  <option value="cosine">cosine</option>
                  <option value="euclidean">euclidean</option>
                  <option value="euclidean_l2">euclidean_l2</option>
                </select>
              </label>
            </div>
          </div>
        ) : null}

        {mode === "public" ? (
          <TurnstileChallenge
            key={captchaResetKey}
            action="public_test"
            onTokenChange={setCaptchaToken}
          />
        ) : null}

        </fieldset>
        {guided && inputSource === "own" ? <div className={styles.uploadGuidance}><p>JPG, PNG or WebP · Up to 4.5 MB combined.</p><p>Your photos aren’t saved in request history.</p></div> : null}
        <div className={cn("mt-5 flex flex-wrap items-center justify-between gap-3", isPlayground && styles.submitRow)}>
          {guided && !isCompact ? <p className="text-xs text-[var(--muted)]"><span className="font-mono text-[var(--text)]">POST {endpoint}</span> · {modelName || "Loading models…"}</p> : null}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              isLoading ||
              Boolean(isBlocked) ||
              modelOptions.length === 0 ||
              (mode === "public" && !captchaToken)
            }
            size="lg"
            className={cn("max-w-full", isPlayground && styles.submitButton)}
          >
            {isPlayground && isLoading ? <LoaderCircle data-icon="inline-start" aria-hidden="true" className={styles.spinner} /> : null}
            {isLoading ? "Waiting for result…" : errorState && errorState.code !== "request_pending" ? "Try again" : guided ? inputSource === "sample" ? "Run sample" : "Run my request" : "Run test"}
            {isPlayground && !isLoading ? <ArrowRight data-icon="inline-end" aria-hidden="true" /> : null}
          </Button>
        </div>

        {!isPlayground ? pendingStatus : null}
      </section>

      {isPlayground && !result ? (
        <section className={styles.responsePane} aria-label="API response">
          <div className={styles.responseHeader}><h3>Response</h3><code>JSON</code></div>
          {isLoading ? pendingStatus : <div className={styles.emptyResponse}><Braces aria-hidden="true" /><p>{errorMessage ? "Fix the request and try again." : "Run a request to see the response."}</p></div>}
        </section>
      ) : null}
      {result ? (
        <section ref={resultRef} tabIndex={-1} aria-label="Completed API result" className={isPlayground ? `${styles.responsePane} min-w-0 focus:outline-none` : "min-w-0 space-y-4 rounded-2xl border border-[var(--accent-2)]/40 bg-[var(--accent-2)]/5 p-4 focus:outline-none sm:p-5"}>
          {isPlayground ? <div className={styles.responseHeader}><h3>Response</h3>{resultJsonToggle}</div> : null}
          <div>
            <p role="status" className="text-xs uppercase tracking-[0.2em] text-[var(--accent-2)]">{isSimulatedResult ? "Preview response" : "Request complete"}{guided && resultSource === "sample" ? " · sample image" : ""}</p>
            <h4 className="mt-2 text-xl text-[var(--text)]">{isSimulatedResult ? "Simulated response" : noFaceResult ? "No face found." : representSummary ? guided ? "Embedding ready." : `${representSummary.faceCount === 1 ? "Face" : `${representSummary.faceCount} faces`} detected. Embedding ready.` : verifyResult ? verifyResult.verified ? "The faces are a match at this threshold." : "The faces are not a match at this threshold." : "Your comparison is ready."}</h4>
            <p className="mt-2 text-sm text-[var(--muted)]">{isSimulatedResult ? "No photos were analysed. This preview shows the response format only." : noFaceResult ? "Detection completed, but no face was found in one or more images. No match decision or embedding was produced. This completed call is billable." : guided ? representSummary ? `${representSummary.faceCount} ${representSummary.faceCount === 1 ? "face" : "faces"} · ${representSummary.vectorLength} numbers per face. Use the same model to compare embeddings.` : verifyResult ? `Distance ${verifyResult.distance} · threshold ${verifyResult.threshold}. Lower distances mean more similar faces.` : "Lower distances mean more similar faces." : representSummary ? `The API returned ${representSummary.vectorLength} numbers per face. Compare embeddings made with the same model to measure facial similarity. An embedding does not identify a person by name.` : verifyResult ? "A lower distance means the faces are more similar. The threshold determines the match decision; a non-match is a valid API result." : "Each distance describes similarity to a supplied vector. Lower distances mean greater similarity."}</p>
          </div>
          {guided && result.activation_progress_saved === false ? <p className="text-sm text-[var(--muted)]">Result complete. Progress wasn’t saved; keep this open.</p> : null}
          {!isPlayground ? <div className="flex items-center justify-between gap-3">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">Result</p>
            {resultJsonToggle}
          </div> : null}

          {verifyResult && !isCompact && !isSimulatedResult ? (
            <div className="grid gap-3 sm:grid-cols-4">
              <div
                className={`rounded-xl border p-3 ${
                  verifyResult.verified
                    ? "border-[var(--accent-2)]/60 bg-[var(--accent-2)]/10"
                    : "border-[var(--outline)] bg-[var(--bg)]/20"
                }`}
              >
                <p
                  className={`text-2xs uppercase tracking-[0.3em] ${
                    verifyResult.verified ? "text-[var(--accent-2)]" : "text-[var(--muted)]"
                  }`}
                >
                  Decision
                </p>
                <p className="mt-1 text-sm text-[var(--text)]">
                  {verifyResult.verified ? "Match" : "No match"}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Distance</p>
                <p className="mt-1 text-sm text-[var(--text)]">{verifyResult.distance}</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Threshold</p>
                <p className="mt-1 text-sm text-[var(--text)]">{verifyResult.threshold}</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Model</p>
                <p className="mt-1 text-sm text-[var(--text)]">{verifyResult.modelName}</p>
              </div>
            </div>
          ) : null}

          {representSummary && !isCompact && !isSimulatedResult ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Faces</p>
                <p className="mt-1 text-sm text-[var(--text)]">{representSummary.faceCount}</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Vector length</p>
                <p className="mt-1 text-sm text-[var(--text)]">{representSummary.vectorLength}</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Model</p>
                <p className="mt-1 text-sm text-[var(--text)]">{representSummary.modelName || modelName}</p>
              </div>
            </div>
          ) : null}

          {compareSummary && !isCompact && !isSimulatedResult ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Metric</p>
                <p className="mt-1 text-sm text-[var(--text)]">{compareSummary.metric}</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Compared vectors</p>
                <p className="mt-1 text-sm text-[var(--text)]">{compareSummary.rows}</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Best distance</p>
                <p className="mt-1 text-sm text-[var(--text)]">{formatNumber(compareSummary.bestDistance)}</p>
              </div>
            </div>
          ) : null}

          {guided && resultSource === "sample" ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
              {!isCompact ? <p className="max-w-md text-sm text-[var(--muted)]">Next, try a photo of your own.</p> : null}
              <button type="button" onClick={() => { setInputSource("own"); setResult(null); }} className="min-h-11 rounded-full border border-[var(--accent)] px-4 py-2 text-sm text-[var(--accent)] hover:bg-[var(--accent)]/10">Try my images</button>
            </div>
          ) : null}
          {showRawJson ? (
            <pre className="max-h-80 overflow-auto rounded-xl border border-border bg-background p-3 text-xs text-[var(--muted)]">
              {JSON.stringify(result, null, 2)}
            </pre>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
